import { useEffect, useMemo, useState } from "react";
import { useIsEdgeLocal } from "../../../hooks/useEdgeMode";
import { useGuestHistory } from "../../../hooks/useReservations";
import { phoneError } from "../logic/guestFields";
import { knownGuestOf, type GuestLookupAnswer } from "../logic/guestHistory";

// Изчакване след последната натисната цифра — заявка за всяка е излишна.
export const GUEST_LOOKUP_DELAY_MS = 400;

/**
 * Познат ли е телефонът във формата „Нова резервация". Пита BE само за
 * ВАЛИДЕН номер (правилото на полето) и чак след като писането спре; връща
 * отговора САМО за текущия телефон — докато се пише или чака, е null (чипът
 * изчезва, името не се пипа). В локален режим не пита: историята е в облака,
 * касата я няма. `excludeId` — редактираната резервация.
 */
export const useGuestHistoryLookup = ({
  phone,
  excludeId,
  enabled,
}: {
  phone: string;
  excludeId: number | null;
  // Формата е отворена и модулът работи (иначе без заявки).
  enabled: boolean;
}): GuestLookupAnswer | null => {
  const isEdgeLocal = useIsEdgeLocal();
  const trimmedPhone = phone.trim();
  const lookupPhone = enabled && !isEdgeLocal && phoneError(trimmedPhone) === null ? trimmedPhone : null;

  // Телефонът, при който писането е спряло.
  const [settledPhone, setSettledPhone] = useState<string | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettledPhone(lookupPhone), GUEST_LOOKUP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [lookupPhone]);

  const isSettled = settledPhone !== null && settledPhone === lookupPhone;
  const { data } = useGuestHistory(settledPhone, excludeId, { enabled: isSettled });
  const answerData = isSettled ? data : undefined;

  // Същата референция, докато отговорът не се смени — формата сравнява по нея.
  return useMemo(
    () => (answerData === undefined || lookupPhone === null ? null : { phone: lookupPhone, history: knownGuestOf(answerData) }),
    [answerData, lookupPhone],
  );
};
